/**
 * <s-category-drawer> — side-by-side category columns inside the category-drawer dialog.
 *
 * Markup contract:
 *   .pit-cat-panel[data-cat-title]  — column-2 panel; its title fills the drawer header.
 *   [data-cat-sub][aria-controls]  — section button; shows its column-3 panel.
 *   .pit-cat-panel[hidden]         — panels toggled by this component.
 *   data-l2-open / data-l3-open    — set on the host so CSS reveals columns 2 and 3.
 *
 * Header triggers (outside the dialog) use
 *   commandfor="category-drawer" command="show-modal" data-open-category="<panel id>"
 * and this module pre-selects that panel. Closing the dialog resets every column.
 */
class SCategoryDrawer extends HTMLElement {
  #ac = new AbortController();

  connectedCallback() {
    const { signal } = this.#ac;
    this.addEventListener('click', (event) => {
      const sub = event.target.closest('[data-cat-sub]');
      if (sub) this.#selectSub(sub);
    }, { signal });
    this.closest('dialog')?.addEventListener('close', () => this.reset(), { signal });
  }

  disconnectedCallback() {
    this.#ac.abort();
  }

  selectTop(panelId) {
    const panel = this.querySelector(`#${CSS.escape(panelId)}`);
    if (!panel) return;
    for (const candidate of this.querySelectorAll('[data-cat-col="2"] > .pit-cat-panel')) {
      candidate.hidden = candidate !== panel;
    }
    this.#clearLevel3();
    this.toggleAttribute('data-l2-open', true);
    this.#setTitle(panel.dataset.catTitle);
  }

  focusFirst(panelId) {
    this.querySelector(`#${CSS.escape(panelId)} a[href], #${CSS.escape(panelId)} button`)?.focus();
  }

  #setTitle(text) {
    const title = document.getElementById('category-drawer-title');
    if (!title) return;
    title.dataset.defaultTitle ??= title.textContent;
    title.textContent = text || title.dataset.defaultTitle;
  }

  #selectSub(button) {
    const panelId = button.getAttribute('aria-controls');
    const panel = this.querySelector(`#${CSS.escape(panelId)}`);
    if (!panel) return;
    for (const sub of this.querySelectorAll('[data-cat-sub]')) {
      sub.setAttribute('aria-expanded', String(sub === button));
    }
    for (const candidate of this.querySelectorAll('[data-cat-col="3"] > .pit-cat-panel')) {
      candidate.hidden = candidate !== panel;
    }
    this.toggleAttribute('data-l3-open', true);
  }

  #clearLevel3() {
    for (const sub of this.querySelectorAll('[data-cat-sub]')) sub.setAttribute('aria-expanded', 'false');
    for (const panel of this.querySelectorAll('[data-cat-col="3"] > .pit-cat-panel')) panel.hidden = true;
    this.removeAttribute('data-l3-open');
  }

  reset() {
    this.#setTitle('');
    for (const panel of this.querySelectorAll('[data-cat-col="2"] > .pit-cat-panel')) panel.hidden = true;
    this.#clearLevel3();
    this.removeAttribute('data-l2-open');
  }
}

if (!customElements.get('s-category-drawer')) customElements.define('s-category-drawer', SCategoryDrawer);

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-open-category]');
  if (!trigger) return;
  const drawer = document.querySelector('s-category-drawer');
  if (!drawer) return;
  const panelId = trigger.dataset.openCategory;
  drawer.selectTop(panelId);
  requestAnimationFrame(() => {
    drawer.focusFirst(panelId);
  });
});
